import PropTypes from "prop-types"
import { useEffect, useRef } from "react"
import { connect } from "react-redux"

import ErrorGroup from "areas/product/error-group"
import {
  diamondStartOver,
  forceHideMiniview,
  toggleBuildYourOwn,
} from "areas/product/store/actions"
import Button from "components/ui/button"
import { cn } from "styles/utils"

import AdditionalInfo from "./additional-info"
import CaratOptions from "./carat-options"
import ChooseGemstones from "./choose-gemstones"
import ChooseStone from "./choose-stone"
import ChooseStoneTypes from "./choose-stone-types"
import FinalDiamond from "./final-diamond"
import Heading from "./heading"
import Navigation from "./navigation"
import ShapeOptions from "./shape-options"

const InternalDiamondSelector = ({
  currentStepIndex,
  currentStepName,
  diamondLoaded,
  errorText,
  openedPanel,
  returnToRec,
  startOver,
  toggleHideMiniview,
}) => {
  if (!diamondLoaded) {
    return null
  }
  const diamondSelectorRef = useRef(null)
  const chooseStone = <ChooseStone />
  const infoComponent = <AdditionalInfo />

  useEffect(() => {
    toggleHideMiniview(openedPanel)
  }, [openedPanel])

  let content
  let additionalInfo
  switch (currentStepName) {
    case "choose_stone_type":
      content = <ChooseStoneTypes />
      break
    case "choose_gemstone":
      content = <ChooseGemstones />
      additionalInfo = infoComponent
      break
    case "d_shape":
      content = <ShapeOptions />
      break
    case "d_carat":
      content = <CaratOptions />
      break
    case "choose_stone":
      content = chooseStone
      additionalInfo = infoComponent
      break
    case "d_cut":
    case "d_color":
    case "d_clarity":
      content = chooseStone
      additionalInfo = (
        <div className="text-center">
          {infoComponent}
          <Button
            className="font-light"
            onClick={() => returnToRec()}
            text
            underline
            variant="secondary"
          >
            Return To Recommendations
          </Button>
        </div>
      )
      break
    case "confirm_stone":
      content = chooseStone
      break
    case "confirm_gemstone":
    case "final_diamond":
      content = <FinalDiamond step={currentStepName} />
      break
  }

  return (
    <div
      className={cn("mb-24 px-16", currentStepIndex === 0 && "pt-28")}
      ref={diamondSelectorRef}
    >
      {currentStepIndex > 0 && (
        <Button
          className="text-14 mb-12 h-16 font-light"
          data-cy="diamon-selector-start-over"
          onClick={() => startOver()}
          text
          underline
          variant="secondary"
        >
          Start Over
        </Button>
      )}
      {errorText && <ErrorGroup>{errorText}</ErrorGroup>}

      <Heading />
      <div className="mt-24">{content}</div>

      <Navigation diamondSelectorRef={diamondSelectorRef} />
      {additionalInfo}
    </div>
  )
}

const mapStateToProps = (state) => {
  return {
    currentStepIndex: state.diamondUI.currentStep,
    currentStepName: state.diamondUI.steps[state.diamondUI.currentStep],
    diamondLoaded: state.diamondSelector.diamondLoaded,
    errorText: state.choices.s1?.error,
    openedPanel: state.panel.openedPanelIndexes.includes("diamond"),
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    returnToRec: () => {
      dispatch(toggleBuildYourOwn())
    },
    startOver: () => {
      dispatch(diamondStartOver())
    },
    toggleHideMiniview: (hide) => {
      dispatch(forceHideMiniview("scroll", hide))
    },
  }
}

InternalDiamondSelector.propTypes = {
  currentStepIndex: PropTypes.number,
  currentStepName: PropTypes.string,
  diamondLoaded: PropTypes.bool,
  errorText: PropTypes.string,
  openedPanel: PropTypes.bool,
  returnToRec: PropTypes.func,
  startOver: PropTypes.func,
  toggleHideMiniview: PropTypes.func,
}

const DiamondSelector = connect(
  mapStateToProps,
  mapDispatchToProps
)(InternalDiamondSelector)

export default DiamondSelector
